import { Fragment, useState } from "react";
import { Select, SelectItem, TextInput, Toggle } from "@carbon/react";
import {
  BIDDING_TYPES,
  PAYMENT_METHOD_LIST,
  INCOTERM_LIST,
  CURRENCY_LIST,
  getBiddingWindowErrors,
  isScheduledStart,
  todayInArgentina,
} from "../../../utils";
import i18next from "../../../i18n";
import { MaterialType } from "../../../types";
import GeneralDataFiles from "./GeneralDataFiles";

const req = <span className="order-required"> *</span>;

type MultipleMaterialsBiddingFormProps = {
  type: string;
  actions: any;
  generalBidData: any;
  materialsToBid: any;
  setGeneralBidData: any;
  isLoadingUpload: boolean;
  isUploadSuccess: boolean;
  uploadedFileUrl: string | null;
  handleSpecification: any;
  handleSupplyPeriod: any;
  onApplyDatesToAll?: (source: any) => void;
  consultationViolation?: boolean;
};

/** Step 5: the single-record "general data" form (dates, payment, files,
 * comment). Carbon fields bound to the shared reducer (setGeneralBidData). */
const MultipleMaterialsBiddingForm = ({
  type,
  actions,
  generalBidData,
  materialsToBid,
  setGeneralBidData,
  isLoadingUpload,
  isUploadSuccess,
  uploadedFileUrl,
  handleSpecification,
  consultationViolation = false,
}: MultipleMaterialsBiddingFormProps) => {
  const [showConsultation, setShowConsultation] = useState(
    !!generalBidData.consultationStartValue
  );

  const handleToggleConsultation = () => {
    const next = !showConsultation;
    setShowConsultation(next);
    if (!next) {
      setGeneralBidData({
        type: actions.UPDATE_CONSULTATION_START,
        consultationStartValue: "",
      });
      setGeneralBidData({
        type: actions.UPDATE_CONSULTATION_END,
        consultationEndValue: "",
      });
    }
  };

  const isService = materialsToBid.some(
    (material: any) => material.type === MaterialType.SERVICES
  );

  const windowErrors = getBiddingWindowErrors(generalBidData);
  const scheduled = isScheduledStart(generalBidData.startDateValue);

  return (
    <Fragment>
      <form className="order-general" autoComplete="off">
        {(type === BIDDING_TYPES.JOINT || type === BIDDING_TYPES.INVERSE_JOINT) && (
          <div className="order-grid">
            <TextInput
              id="name"
              labelText={<>{i18next.t("tenders.form.name")}{req}</>}
              value={generalBidData.name ?? ""}
              onChange={(e) => {
                if (e.target.value.length === 255) return;
                setGeneralBidData({
                  type: actions.UPDATE_NAME,
                  name: e.target.value,
                });
              }}
            />
            <TextInput
              id="internalCode"
              labelText={i18next.t("tenders.form.internalCode")}
              value={generalBidData.internalCode ?? ""}
              onChange={(e) =>
                setGeneralBidData({
                  type: actions.UPDATE_INTERNAL_CODE,
                  internalCode: e.target.value,
                })
              }
            />
          </div>
        )}

        {actions.UPDATE_CONSULTATION_START && (
          <Toggle
            id="use-consultation-period"
            size="sm"
            labelText={i18next.t("tenders.form.useConsultationPeriod", {
              defaultValue: "Usar período de consulta",
            })}
            labelA=""
            labelB=""
            toggled={showConsultation}
            onToggle={handleToggleConsultation}
          />
        )}

        <div className="order-grid--dates">
          {actions.UPDATE_CONSULTATION_START && showConsultation && (
            <>
              <TextInput
                id="consultationStart"
                type="date"
                labelText={i18next.t("tenders.form.consultationStart")}
                min={todayInArgentina()}
                value={generalBidData.consultationStartValue ?? ""}
                onChange={(e) =>
                  setGeneralBidData({
                    type: actions.UPDATE_CONSULTATION_START,
                    consultationStartValue: e.target.value,
                  })
                }
              />
              <TextInput
                id="consultationEnd"
                type="date"
                labelText={i18next.t("tenders.form.consultationEnd")}
                min={generalBidData.consultationStartValue}
                disabled={!generalBidData.consultationStartValue}
                value={generalBidData.consultationEndValue ?? ""}
                onChange={(e) =>
                  setGeneralBidData({
                    type: actions.UPDATE_CONSULTATION_END,
                    consultationEndValue: e.target.value,
                  })
                }
              />
            </>
          )}

          <div>
            <TextInput
              id="startDate"
              type="date"
              labelText={<>{i18next.t("tenders.form.initialDate")}{req}</>}
              min={generalBidData.consultationEndValue || todayInArgentina()}
              value={generalBidData.startDateValue ?? ""}
              invalid={consultationViolation}
              invalidText={i18next.t("orderByRequest.consultationStartConflict")}
              onChange={(e) =>
                setGeneralBidData({
                  type: actions.UPDATE_START_DATE,
                  startDateValue: e.target.value,
                })
              }
            />
          </div>

          <TextInput
            id="startTime"
            type="time"
            step={60}
            labelText={<>{i18next.t("tenders.form.openTime")}{scheduled && req}</>}
            helperText={scheduled ? undefined : i18next.t("tenders.form.openTimeHelper")}
            disabled={!generalBidData.startDateValue}
            value={generalBidData.startTimeValue ?? ""}
            invalid={!!windowErrors.startTime}
            invalidText={windowErrors.startTime && i18next.t(windowErrors.startTime)}
            onChange={(e) =>
              setGeneralBidData({
                type: actions.UPDATE_START_TIME,
                startTimeValue: e.target.value,
              })
            }
          />

          <TextInput
            id="finishDate"
            type="date"
            labelText={<>{i18next.t("tenders.form.closeDate")}{req}</>}
            min={generalBidData.startDateValue}
            disabled={!generalBidData.startDateValue}
            value={generalBidData.endDateValue ?? ""}
            onChange={(e) =>
              setGeneralBidData({
                type: actions.UPDATE_FINISH_DATE,
                endDateValue: e.target.value,
              })
            }
          />

          <TextInput
            id="finishTime"
            type="time"
            step={60}
            labelText={<>{i18next.t("tenders.form.closeTime")}{req}</>}
            helperText={i18next.t("tenders.form.argentinaTime")}
            disabled={!generalBidData.endDateValue}
            value={generalBidData.endTimeValue ?? ""}
            invalid={!!windowErrors.endTime}
            invalidText={windowErrors.endTime && i18next.t(windowErrors.endTime)}
            onChange={(e) =>
              setGeneralBidData({
                type: actions.UPDATE_FINISH_TIME,
                endTimeValue: e.target.value,
              })
            }
          />
        </div>

        <div className="order-grid--dates">
          <Select
            id="paymentMethod"
            labelText={<>{i18next.t("tenders.form.paymentMethod")}{req}</>}
            value={generalBidData.paymentMethod}
            onChange={(e) =>
              setGeneralBidData({
                type: actions.UPDATE_PAYMENT_METHOD,
                paymentMethod: e.target.value,
              })
            }
          >
            <SelectItem value="" text={i18next.t("validations.selectPaymentMethod")} />
            {PAYMENT_METHOD_LIST?.map((pay) => (
              <SelectItem key={pay.id} value={pay.name} text={pay.name} />
            ))}
          </Select>

          <TextInput
            id="paymentTerm"
            type="number"
            min={0}
            labelText={<>{i18next.t("tenders.paymentTerm")}{req}</>}
            value={generalBidData.paymentTerm ?? ""}
            onChange={(e) =>
              setGeneralBidData({
                type: actions.UPDATE_PAYMENT_TERM,
                paymentTerm: e.target.value,
              })
            }
          />

          {!isService && (
            <Select
              id="incoTerm"
              labelText={<>{i18next.t("tenders.form.incoTerm")}{req}</>}
              value={generalBidData.incoTerm}
              onChange={(e) =>
                setGeneralBidData({
                  type: actions.UPDATE_INCOTERM,
                  incoTerm: e.target.value,
                })
              }
            >
              <SelectItem value="" text={i18next.t("validations.selectIncoterm")} />
              {INCOTERM_LIST?.map((incoterm) => (
                <SelectItem
                  key={incoterm.id}
                  value={incoterm.name}
                  text={incoterm.name}
                />
              ))}
            </Select>
          )}

          {!isService && (
            <Select
              id="currency"
              labelText={<>{i18next.t("tenders.form.currency")}{req}</>}
              value={generalBidData.currency}
              onChange={(e) =>
                setGeneralBidData({
                  type: actions.UPDATE_CURRENCY,
                  currency: e.target.value,
                })
              }
            >
              <SelectItem value="" text={i18next.t("validations.selectCurrency")} />
              {CURRENCY_LIST?.map((currency) => (
                <SelectItem
                  key={currency.id}
                  value={currency.name}
                  text={currency.name}
                />
              ))}
            </Select>
          )}
        </div>

        {!isService && (
          <Toggle
            id="buyer-picks-up"
            size="sm"
            labelText={i18next.t("tenders.form.buyerPicksUp")}
            labelA=""
            labelB=""
            toggled={Boolean(generalBidData.buyerPicksUp)}
            onToggle={(checked) =>
              setGeneralBidData({
                type: actions.UPDATE_BUYER_PICKS_UP,
                buyerPicksUp: checked,
              })
            }
          />
        )}

        {!isService && (
          <Toggle
            id="expire-date"
            size="sm"
            labelText={i18next.t("tenders.form.expireDate")}
            labelA=""
            labelB=""
            toggled={generalBidData.expireDate}
            onToggle={(checked) =>
              setGeneralBidData({
                type: actions.UPDATE_EXPIRE_DATE,
                expireDate: checked,
              })
            }
          />
        )}

        <GeneralDataFiles
          files={generalBidData.files}
          isLoadingUpload={isLoadingUpload}
          isUploadSuccess={isUploadSuccess}
          uploadedFileUrl={uploadedFileUrl}
          handleSpecification={handleSpecification}
          onAddFile={(file) =>
            setGeneralBidData({ type: actions.ADD_FILE, files: [file] })
          }
          onRemoveFile={(index) =>
            setGeneralBidData({ type: actions.REMOVE_FILE, index })
          }
        />

        <TextInput
          id="comment"
          labelText={
            <>
              {i18next.t("tenders.form.comments")}{" "}
              <span className="order-optional">
                ({i18next.t("newMaterialForm.optional")})
              </span>
            </>
          }
          placeholder={i18next.t("tenders.form.comments")}
          value={generalBidData.comment ?? ""}
          onChange={(e) => {
            if (e.target.value.length === 255) return;
            setGeneralBidData({
              type: actions.UPDATE_COMMENT,
              comment: e.target.value,
            });
          }}
        />
      </form>
    </Fragment>
  );
};

export default MultipleMaterialsBiddingForm;
